"use client";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DownloadIcon } from "@/components/ui/download";
import { PageHeader } from "@/components/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableList } from "@/components/ui/searchable-list";
import { Skeleton } from "@/components/ui/skeleton";
import { SyncHistoryPanel } from "@/components/sync-history-panel";
import { SyncProjectCentralUsersDialog } from "@/components/sync-project-central-users-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserPanel } from "@/components/user-panel";
import { useSearchableList } from "@/hooks/use-searchable-list";
import { supabase } from "@/lib/supabase";
import { HistoryIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

// ─── Types ─────────────────────────────────────────────────────────────────────

type TeamRef = { id: string; name: string };

type UserSummary = {
  user_id: string;
  display_name: string | null;
  username: string | null;
  role: string | null;
  team: string | null;
  job_role: string | null;
  employment_status: string;
  deactivated_at: string | null;
  teams: TeamRef[];
  has_onboarded: boolean;
  email: string | null;
  last_sign_in_at: string | null;
  signed_up_at: string | null;
  file_count: number;
  agent_count: number;
  prompt_count: number;
  team_count: number;
  is_active: boolean;
};

type AllTeam = { id: string; name: string; is_home: boolean };

type UsersCursor = { createdAt: string; userId: string };

// ─── Constants ─────────────────────────────────────────────────────────────────

const USERS_PAGE_SIZE = 25;
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL ?? "";
const SUPABASE_KEY = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "";

const ROLE_FILTER_OPTIONS = [
  { value: "all", label: "All roles" },
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
  { value: "superadmin", label: "Superadmin" },
] as const;

const NO_TEAM_FILTER = "__none__";

// ─── Helpers ───────────────────────────────────────────────────────────────────

const getDisplayName = (user: Pick<UserSummary, "display_name" | "username" | "email">) =>
  user.display_name?.trim() || user.username?.trim() || user.email?.split("@")[0] || "Unknown";

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2) || "?";

const formatDate = (iso: string | null) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(iso));
};

const roleLabel = (role: string | null) => {
  if (role === "superadmin") return "Superadmin";
  if (role === "admin") return "Admin";
  return "User";
};

const roleBadgeVariant = (role: string | null): "default" | "secondary" | "outline" => {
  if (role === "superadmin") return "default";
  if (role === "admin") return "secondary";
  return "outline";
};

// Every department_type enum value (supabase/migrations — 20250917000001,
// 20251002000001, 20260901000001), spelled out explicitly rather than relying
// on the split("_")+capitalize fallback below for values that happen to look
// right today but aren't guaranteed to (e.g. acronyms).
const DEPARTMENT_LABELS: Record<string, string> = {
  land_development: "Land Development",
  "3_waters": "3 Waters",
  survey: "Survey",
  geospatial: "Geospatial",
  executive: "Executive",
  co: "C&O",
  it: "IT",
  puds: "PUDS",
  engineering: "Engineering",
  data_ai: "Data & AI",
};

const departmentLabel = (department: string | null) => {
  if (!department) return "—";
  return (
    DEPARTMENT_LABELS[department] ??
    department
      .split("_")
      .map((w) => w[0]?.toUpperCase() + w.slice(1))
      .join(" ")
  );
};

function usersCursorFromRow(user: UserSummary): UsersCursor {
  return { createdAt: user.signed_up_at ?? "", userId: user.user_id };
}

async function fetchUsersPage(
  cursor: UsersCursor | null,
  pageSize: number,
  search: string,
  role: string,
  teamId: string,
  sortBy: string
): Promise<{ users: UserSummary[]; totalCount: number }> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token ?? "";
  const res = await fetch(`${SUPABASE_URL}/functions/v1/get-users`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, apikey: SUPABASE_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ cursor, pageSize, search, role, teamId, sortBy }),
  });
  const json = await res.json();
  if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load users.");
  return { users: json.users as UserSummary[], totalCount: json.totalCount as number };
}

function UsersTableSkeletonRow() {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell>
        <div className="flex items-center gap-3">
          <Skeleton className="size-7 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-32 rounded" />
            <Skeleton className="h-3 w-40 rounded" />
          </div>
        </div>
      </TableCell>
      <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
      <TableCell><Skeleton className="h-3.5 w-20 rounded" /></TableCell>
      <TableCell><Skeleton className="h-3.5 w-24 rounded" /></TableCell>
      <TableCell><Skeleton className="h-3.5 w-20 rounded" /></TableCell>
      <TableCell><Skeleton className="h-3.5 w-16 rounded" /></TableCell>
      <TableCell><Skeleton className="h-3.5 w-16 rounded" /></TableCell>
      <TableCell className="text-right"><Skeleton className="ml-auto h-3.5 w-6 rounded" /></TableCell>
      <TableCell className="text-right"><Skeleton className="ml-auto h-3.5 w-6 rounded" /></TableCell>
      <TableCell className="text-right"><Skeleton className="ml-auto h-3.5 w-6 rounded" /></TableCell>
    </TableRow>
  );
}

function UsersTableSkeleton() {
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[220px]">User</TableHead>
          <TableHead className="w-[90px]">Role</TableHead>
          <TableHead className="w-[110px]">Department</TableHead>
          <TableHead className="w-[170px]">Position</TableHead>
          <TableHead className="w-[130px]">Team</TableHead>
          <TableHead className="w-[100px]">Signed up</TableHead>
          <TableHead className="w-[100px]">Last active</TableHead>
          <TableHead className="w-[60px] text-right">Files</TableHead>
          <TableHead className="w-[60px] text-right">Agents</TableHead>
          <TableHead className="w-[70px] text-right">Teams</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 8 }).map((_, i) => (
          <UsersTableSkeletonRow key={i} />
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Users list view ───────────────────────────────────────────────────────────

function UsersListView({
  list,
  allTeams,
  roleFilter,
  setRoleFilter,
  teamFilter,
  setTeamFilter,
  sortFilter,
  setSortFilter,
  totalCount,
  onSelectUser,
  onBack,
  onSyncUsers,
  onViewSyncHistory,
}: {
  list: ReturnType<typeof useSearchableList<UserSummary, UsersCursor>>;
  allTeams: AllTeam[];
  roleFilter: string;
  setRoleFilter: (value: string) => void;
  teamFilter: string;
  setTeamFilter: (value: string) => void;
  sortFilter: string;
  setSortFilter: (value: string) => void;
  totalCount: number | null;
  onSelectUser: (user: UserSummary) => void;
  onBack?: () => void;
  onSyncUsers?: () => void;
  onViewSyncHistory?: () => void;
}) {
  const { items: users } = list;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Users"
        description="Manage users, roles, and team memberships"
        onBack={onBack}
        actions={
          <div className="flex items-center gap-1.5">
            {onViewSyncHistory && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={onViewSyncHistory}
                  >
                    <HistoryIcon className="size-3.5" />
                    <span className="hidden sm:inline">Sync history</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="sm:hidden">
                  Sync history
                </TooltipContent>
              </Tooltip>
            )}
            {onSyncUsers && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onSyncUsers}>
                    <DownloadIcon size={14} />
                    <span className="hidden sm:inline">Sync users</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="sm:hidden">
                  Sync users
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        }
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 pt-4">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 pb-3">
          <span className="text-sm text-muted-foreground">
            {totalCount === null ? "…" : totalCount} user{totalCount !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger size="sm" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger size="sm" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teams</SelectItem>
                <SelectItem value={NO_TEAM_FILTER}>No team</SelectItem>
                {allTeams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortFilter} onValueChange={setSortFilter}>
              <SelectTrigger size="sm" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
              </SelectContent>
            </Select>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  onClick={() => list.reload()}
                  disabled={list.loading}
                  aria-label="Refresh"
                >
                  <RefreshCwIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Refresh</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <SearchableList
          state={list}
          label="users"
          placeholder="Search by name or email…"
          className="max-w-6xl"
          emptyState={<p className="py-16 text-center text-sm text-muted-foreground">No users found.</p>}
          listClassName="rounded-md border border-border overflow-x-auto"
          skeleton={
            <div className="rounded-md border border-border overflow-x-auto">
              <UsersTableSkeleton />
            </div>
          }
        >
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[220px]">User</TableHead>
                <TableHead className="w-[90px]">Role</TableHead>
                <TableHead className="w-[110px]">Department</TableHead>
                <TableHead className="w-[170px]">Position</TableHead>
                <TableHead className="w-[130px]">Team</TableHead>
                <TableHead className="w-[100px]">Signed up</TableHead>
                <TableHead className="w-[100px]">Last active</TableHead>
                <TableHead className="w-[60px] text-right">Files</TableHead>
                <TableHead className="w-[60px] text-right">Agents</TableHead>
                <TableHead className="w-[70px] text-right">Teams</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const name = getDisplayName(user);
                return (
                  <TableRow
                    key={user.user_id}
                    className="cursor-pointer hover:bg-accent/50"
                    onClick={() => onSelectUser(user)}
                  >
                    <TableCell className="overflow-hidden text-sm">
                      <div className="flex items-center gap-3">
                        <Avatar className="size-7 shrink-0">
                          <AvatarFallback className="bg-foreground text-background text-xs font-medium">
                            {getInitials(name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-normal text-foreground">{name}</span>
                            {user.is_active && !user.deactivated_at && (
                              <span
                                className="size-1.5 shrink-0 rounded-full bg-green-500"
                                title="Active in last 30 days"
                              />
                            )}
                            {user.deactivated_at && <Badge variant="outline" className="shrink-0 text-[10px]">Inactive</Badge>}
                            {user.employment_status === "not_active" && (
                              <Badge
                                variant="outline"
                                className="shrink-0 text-[10px] text-destructive border-destructive/40"
                              >
                                Not with Woods
                              </Badge>
                            )}
                          </div>
                          <p className="truncate text-xs text-muted-foreground">{user.email ?? "—"}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="overflow-hidden text-sm">
                      <Badge variant={roleBadgeVariant(user.role)} className="text-xs">
                        {roleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell className="overflow-hidden truncate text-sm text-muted-foreground">
                      {departmentLabel(user.team)}
                    </TableCell>
                    <TableCell
                      className="overflow-hidden truncate text-sm text-muted-foreground"
                      title={user.job_role ?? undefined}
                    >
                      {user.job_role ?? "—"}
                    </TableCell>
                    <TableCell className="overflow-hidden truncate text-sm text-muted-foreground">
                      {user.teams.length > 0 ? user.teams.map((t) => t.name).join(", ") : "—"}
                    </TableCell>
                    <TableCell className="overflow-hidden truncate text-sm text-muted-foreground">
                      {formatDate(user.signed_up_at)}
                    </TableCell>
                    <TableCell className="overflow-hidden truncate text-sm text-muted-foreground">
                      {formatDate(user.last_sign_in_at)}
                    </TableCell>
                    <TableCell className="overflow-hidden text-right text-sm tabular-nums">
                      {user.file_count}
                    </TableCell>
                    <TableCell className="overflow-hidden text-right text-sm tabular-nums">
                      {user.agent_count}
                    </TableCell>
                    <TableCell className="overflow-hidden text-right text-sm tabular-nums">
                      {user.team_count}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SearchableList>
      </div>
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────────

export type UsersPanelProps = {
  onBack?: () => void;
  userRole?: string | null;
};

export function UsersPanel({ onBack, userRole }: UsersPanelProps) {
  const [allTeams, setAllTeams] = useState<AllTeam[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [detailKey] = useState(0);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [sortFilter, setSortFilter] = useState("newest");
  const [totalCount, setTotalCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void supabase
      .from("teams")
      .select("id, name, is_home")
      .order("name")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[UsersPanel] load teams error:", error);
          toast.error("Failed to load teams.");
          return;
        }
        setAllTeams(((data ?? []) as AllTeam[]).filter((t) => !t.is_home));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPage = useCallback(
    async (cursor: UsersCursor | null, pageSize: number, search: string) => {
      const { users, totalCount: count } = await fetchUsersPage(
        cursor,
        pageSize,
        search,
        roleFilter,
        teamFilter,
        sortFilter
      );
      setTotalCount(count);
      return users;
    },
    [roleFilter, teamFilter, sortFilter]
  );

  const list = useSearchableList<UserSummary, UsersCursor>({
    pageSize: USERS_PAGE_SIZE,
    fetchPage,
    getCursor: usersCursorFromRow,
    getKey: (u) => u.user_id,
    resetKey: `${roleFilter}:${teamFilter}:${sortFilter}`,
  });

  // ── User detail / edit (settings layout) ──
  if (selectedUserId) {
    return (
      <UserPanel
        key={`${selectedUserId}-${detailKey}`}
        userId={selectedUserId}
        allTeams={allTeams}
        onBack={() => setSelectedUserId(null)}
        onSaved={() => list.reload()}
        callerRole={userRole}
      />
    );
  }

  if (showSyncHistory) {
    return <SyncHistoryPanel onBack={() => setShowSyncHistory(false)} />;
  }

  return (
    <>
      <UsersListView
        list={list}
        allTeams={allTeams}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        teamFilter={teamFilter}
        setTeamFilter={setTeamFilter}
        sortFilter={sortFilter}
        setSortFilter={setSortFilter}
        totalCount={totalCount}
        onSelectUser={(user) => setSelectedUserId(user.user_id)}
        onBack={onBack}
        onSyncUsers={() => setSyncDialogOpen(true)}
        onViewSyncHistory={userRole === "superadmin" ? () => setShowSyncHistory(true) : undefined}
      />
      <SyncProjectCentralUsersDialog
        open={syncDialogOpen}
        onOpenChange={setSyncDialogOpen}
        onApplied={() => list.reload()}
      />
    </>
  );
}
